import type { TOC } from '@ember/component/template-only';
import { Text } from 'tio-ui/components/typography';
import type { ComponentLike } from '@glint/template';

/**
 * Color theme options for the stat card
 */
export type StatCardColor =
  | 'emerald'
  | 'blue'
  | 'violet'
  | 'amber'
  | 'teal'
  | 'red'
  | 'green'
  | 'indigo'
  | 'pink'
  | 'gray';

/**
 * StatCard - A metric display card for dashboards and summary sections
 *
 * Displays a key statistic with icon, label, value, and optional subtext.
 * Used throughout the reimagined experience for consistent metric presentation.
 *
 * @example
 * ```gts
 * <StatCard
 *   @icon={{PhCurrencyDollar}}
 *   @label="Annual Benefit"
 *   @value="$5,250"
 *   @subtext="Tax-free under Section 127"
 *   @color="emerald"
 * />
 * ```
 *
 * @example With custom value color
 * ```gts
 * <StatCard
 *   @icon={{PhBank}}
 *   @label="Remaining"
 *   @value="$3,200"
 *   @valueClass="text-emerald-600"
 *   @color="violet"
 * />
 * ```
 */
interface StatCardSignature {
  Args: {
    /** Phosphor icon component to display */
    icon: ComponentLike<{ Args: { size: number }; Element: SVGElement }>;
    /** Label text displayed above the value */
    label: string;
    /** Main value to display (formatted string, e.g. "$42,000") */
    value: string;
    /** Secondary text below the value */
    subtext?: string;
    /** Color theme for icon background */
    color?: StatCardColor;
    /** Optional CSS class for value styling (e.g. for colored values) */
    valueClass?: string;
  };
  Blocks: {
    /** Optional extra content below the value (e.g., progress bar) */
    extra?: [];
  };
  Element: HTMLDivElement;
}

const colorClasses: Record<StatCardColor, { iconBg: string; iconText: string }> = {
  emerald: {
    iconBg: 'bg-emerald-100 dark:bg-emerald-900/50',
    iconText: 'text-emerald-600 dark:text-emerald-400',
  },
  blue: { iconBg: 'bg-blue-100 dark:bg-blue-900/50', iconText: 'text-blue-600 dark:text-blue-400' },
  violet: {
    iconBg: 'bg-violet-100 dark:bg-violet-900/50',
    iconText: 'text-violet-600 dark:text-violet-400',
  },
  amber: {
    iconBg: 'bg-amber-100 dark:bg-amber-900/50',
    iconText: 'text-amber-600 dark:text-amber-400',
  },
  teal: { iconBg: 'bg-teal-100 dark:bg-teal-900/50', iconText: 'text-teal-600 dark:text-teal-400' },
  red: { iconBg: 'bg-red-100 dark:bg-red-900/50', iconText: 'text-red-600 dark:text-red-400' },
  green: {
    iconBg: 'bg-green-100 dark:bg-green-900/50',
    iconText: 'text-green-600 dark:text-green-400',
  },
  indigo: {
    iconBg: 'bg-indigo-100 dark:bg-indigo-900/50',
    iconText: 'text-indigo-600 dark:text-indigo-400',
  },
  pink: { iconBg: 'bg-pink-100 dark:bg-pink-900/50', iconText: 'text-pink-600 dark:text-pink-400' },
  gray: { iconBg: 'bg-gray-100 dark:bg-gray-800', iconText: 'text-gray-600 dark:text-gray-400' },
};

const getColorClasses = (color: StatCardColor = 'gray') => colorClasses[color];

const StatCard: TOC<StatCardSignature> = <template>
  {{#let (getColorClasses @color) as |colors|}}
    <div
      class="bg-white dark:bg-gray-900 rounded-lg p-5 shadow-sm border border-gray-200 dark:border-gray-700"
      role="group"
      aria-label={{@label}}
      ...attributes
    >
      <div class="flex items-center gap-3 mb-3">
        <div class="p-2 {{colors.iconBg}} rounded-lg {{colors.iconText}}">
          {{#let @icon as |Icon|}}
            <Icon @size={{24}} />
          {{/let}}
        </div>
        <Text @intent="muted" class="text-sm">{{@label}}</Text>
      </div>
      <div
        class="text-2xl font-bold {{if @valueClass @valueClass 'text-gray-900 dark:text-white'}}"
      >
        {{@value}}
      </div>
      {{#if @subtext}}
        <Text @intent="muted" class="text-xs mt-1">{{@subtext}}</Text>
      {{/if}}
      {{#if (has-block "extra")}}
        <div class="mt-2">
          {{yield to="extra"}}
        </div>
      {{/if}}
    </div>
  {{/let}}
</template>;

export default StatCard;
